import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { getCsrfToken } from '../utils/api';
import { API_BASE_URL } from '../constants/api';

const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

const API_BASE = API_BASE_URL;
const USER_CACHE_KEY = 'vb_user_cache';

// Axios instance shared by the context — all requests carry credentials (cookies)
const authAxios = axios.create({ baseURL: API_BASE, withCredentials: true });

// Attach CSRF token to all non-GET requests so that state-changing auth calls
// (e.g. logout) pass the server-side CSRF check and are not silently rejected.
authAxios.interceptors.request.use(config => {
  const csrfToken = getCsrfToken();
  if (csrfToken && config.method !== 'get') {
    config.headers['X-CSRF-Token'] = csrfToken;
  }
  return config;
});

// Persist user to localStorage so the next page load restores state instantly,
// eliminating the flash of logged-out state while /auth/me is in flight.
//
// SECURITY: only cache the minimum non-sensitive identity fields. We do NOT
// persist email, phone, totpSecret, refreshToken, payment info, or full
// profile — any of those would be readable by XSS payloads. The cache only
// exists to avoid the logged-out flash; full data is fetched from /auth/me.
const CACHEABLE_USER_FIELDS = ['_id', 'id', 'name', 'avatar', 'role', 'isOrganizer', 'isApproved'];
const pickCacheableUser = (userData) => {
  if (!userData || typeof userData !== 'object') return null;
  const safe = {};
  for (const k of CACHEABLE_USER_FIELDS) {
    if (userData[k] !== undefined) safe[k] = userData[k];
  }
  return safe;
};
const saveUserCache = (userData) => {
  try {
    if (userData) {
      localStorage.setItem(USER_CACHE_KEY, JSON.stringify(pickCacheableUser(userData)));
    } else {
      localStorage.removeItem(USER_CACHE_KEY);
    }
  } catch { /* ignore quota/security errors */ }
};

const loadUserCache = () => {
  try {
    const raw = localStorage.getItem(USER_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  // Initialise from cache immediately — eliminates flash of logged-out state on refresh
  const [user, setUser] = useState(() => loadUserCache());
  const [loading, setLoading] = useState(true);

  const setAndCacheUser = (userData) => {
    setUser(userData);
    saveUserCache(userData);
  };

  // On mount: verify the cached user is still valid via the httpOnly cookie
  useEffect(() => {
    authAxios.get('/auth/me')
      .then(res => setAndCacheUser(res.data))
      .catch(() => {
        // Cookie is invalid or expired — clear everything
        setUser(null);
        saveUserCache(null);
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const login = async (email, password) => {
    const res = await authAxios.post('/auth/login', { email, password });
    const { user: userData } = res.data;
    setAndCacheUser(userData);
    // Fetch full user profile to ensure all fields are present
    try {
      const meRes = await Promise.race([
        authAxios.get('/auth/me'),
        new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 5000)),
      ]);
      setAndCacheUser(meRes.data);
      return meRes.data;
    } catch {
      return userData;
    }
  };

  const register = async (name, email, password, role) => {
    const res = await authAxios.post('/auth/register', { name, email, password, role });
    const { user: userData } = res.data;
    setAndCacheUser(userData);
    return userData;
  };

  const refreshUser = async () => {
    try {
      const res = await authAxios.get('/auth/me');
      setAndCacheUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  };

  // loginWithToken: called after social auth (Google/Apple) — backend has already set the cookie.
  // The token param is kept for mobile backward-compatibility but is not stored in localStorage.
  const loginWithToken = (newToken, userData) => {
    // Cookie is already set by the backend; update state and cache.
    setAndCacheUser(userData);
  };

  const logout = async () => {
    try {
      await authAxios.post('/auth/logout');
    } catch { /* ignore errors */ }
    setUser(null);
    saveUserCache(null);
  };

  return (
    <AuthContext.Provider value={{ user, token: null, login, loginWithToken, register, logout, refreshUser, loading, isAdmin: user?.role === 'admin', isOrganizer: ['organizer', 'admin'].includes(user?.role) }}>
      {children}
    </AuthContext.Provider>
  );
};
